import CartManager from "@scripts/components/cart/CartManager.js";
import Mediator from "@scripts/core/Mediator.js";
import DomManager from "@scripts/core/DomManager.js";
import Toastify from "toastify-js";

export default class ProductQuantityManager {
    constructor(productElement) {
        this.productElement = productElement;

        // Try to get productId from multiple sources
        const addToCartButton = productElement.querySelector("[data-ajax-add-to-cart]");
        this.productId = addToCartButton?.dataset?.ajaxAddToCart ||
            productElement.dataset.productId ||
            this.extractProductIdFromUrl();

        this.cartManager = CartManager.getInstance();
        this.quantity = 1;
        this.cartKey = null;
        this.state = {
            isLoading: false,
            isInCart: false,
            currentQuantity: 0,
            isQuantityControlVisible: false, // Track if quantity control is currently visible
        };

        this.init();
    }

    extractProductIdFromUrl() {
        const link = this.productElement.querySelector("a");
        if (!link) return null;

        const url = new URL(link.href);
        const pathParts = url.pathname.split("/");
        const productIndex = pathParts.indexOf("product");
        if (productIndex !== -1 && pathParts[productIndex + 1]) {
            return pathParts[productIndex + 1];
        }
        return null;
    }

    init() {
        // Create UI immediately WITHOUT checking cart state
        this.#createQuantityControl();

        // Subscribe to cart changes
        Mediator.subscribe("ajax_sage_change_cart", this.#handleCartChange.bind(this));
    }

    #handleCartChange(response) {
        const cartData = response?.data?.values?.cart;
        if (!cartData) {
            return;
        }

        // Check if this product was just added to cart
        const addedProductId = cartData?.added_product_id;

        // Convert both to strings for comparison
        const addedProductIdStr = String(addedProductId);
        const myProductIdStr = String(this.productId);

        // FIRST: Always check if this product is in the cart items
        // items is an object with cart_item_key as key, not an array
        let cartItem = null;
        let cartItemKey = null;

        if (cartData?.items && typeof cartData.items === 'object') {
            for (const key in cartData.items) {
                const item = cartData.items[key];

                if (item.product_id == this.productId || String(item.product_id) === String(this.productId)) {
                    cartItem = item;
                    cartItemKey = key;
                    break;
                }
            }
        }

        if (cartItem) {
            this.cartKey = cartItemKey; // Use the key we found during iteration
            this.state.currentQuantity = cartItem.quantity;
            this.state.isInCart = true;

            // Only update UI if this product was just added OR if quantity control is already visible
            // This prevents showing quantity controls for all products in cart
            const wasJustAdded = addedProductId && addedProductIdStr == myProductIdStr;
            const shouldUpdateUI = wasJustAdded || this.state.isQuantityControlVisible;

            // Update UI
            if (this.state.currentQuantity > 0) {
                if (shouldUpdateUI) {
                    this.#showQuantityControl();
                    this.#updateQuantityDisplay(this.state.currentQuantity);
                }
            } else {
                this.#showBuyButton();
            }

            return;
        }

        // SECOND: If this product was just added (but not found in items yet), use direct data
        if (addedProductId && addedProductIdStr == myProductIdStr) {
            // This EXACT product was just added - use the direct data
            this.cartKey = cartData?.added_item_key;
            this.state.currentQuantity = cartData?.added_item_quantity || 1;
            this.state.isInCart = true;

            // Immediately update UI
            this.#showQuantityControl();
            this.#updateQuantityDisplay(this.state.currentQuantity);

            return;
        }

        // THIRD: Product is NOT in cart items - check if it was removed
        // Only show buy button if we know it was previously in cart
        if (this.state.isInCart) {
            this.state.isInCart = false;
            this.state.currentQuantity = 0;
            this.cartKey = null;
            this.#showBuyButton();
        }
    }

    #createQuantityControl() {
        const addToCartButton = this.productElement.querySelector(
            "[data-ajax-add-to-cart]",
        );

        if (!addToCartButton) {
            return;
        }

        // Check if quantity control already exists
        const existingControl = this.productElement.querySelector(
            `[data-product-quantity-control][data-product-id="${this.productId}"]`
        );

        if (existingControl) {
            this.quantityControl = existingControl;
            this.decreaseButton = existingControl.querySelector("[data-quantity-action='decrease']");
            this.increaseButton = existingControl.querySelector("[data-quantity-action='increase']");
            this.quantityInput = existingControl.querySelector(".quantity-input");

            // Add event listeners again
            this.decreaseButton.addEventListener("click", () => this.#decreaseQuantity());
            this.increaseButton.addEventListener("click", () => this.#increaseQuantity());

            return;
        }

        // Create quantity control container - HIDDEN by default
        const quantityControl = document.createElement("div");
        quantityControl.className =
            "product-quantity-control border border-gray-300 rounded-lg overflow-hidden hidden";

        // Add data attributes for easier identification
        quantityControl.setAttribute("data-product-quantity-control", "");
        quantityControl.setAttribute("data-product-id", this.productId);

        quantityControl.innerHTML = `
      <button
        type="button"
        class="quantity-decrease flex h-12 w-12 items-center justify-center border-r border-gray-300 bg-gray-50 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
        data-quantity-action="decrease"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M5 12h14"/>
        </svg>
      </button>

      <div class="quantity-display flex items-center justify-center min-w-[60px] h-12 bg-white">
        <input
          type="number"
          class="quantity-input w-full text-center font-bold text-gray-900 border-none focus:outline-none focus:ring-0"
          value="1"
          min="0"
          readonly
        />
      </div>

      <button
        type="button"
        class="quantity-increase flex h-12 w-12 items-center justify-center border-l border-gray-300 bg-gray-50 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
        data-quantity-action="increase"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 5v14M5 12h14"/>
        </svg>
      </button>
    `;

        // Insert after the add to cart button
        addToCartButton.parentNode.insertBefore(quantityControl, addToCartButton.nextSibling);

        // Store references
        this.quantityControl = quantityControl;
        this.decreaseButton = quantityControl.querySelector("[data-quantity-action='decrease']");
        this.increaseButton = quantityControl.querySelector("[data-quantity-action='increase']");
        this.quantityInput = quantityControl.querySelector(".quantity-input");

        // Add event listeners
        this.decreaseButton.addEventListener("click", () => this.#decreaseQuantity());
        this.increaseButton.addEventListener("click", () => this.#increaseQuantity());
    }

    #showQuantityControl() {
        // Mark that quantity control is now visible
        this.state.isQuantityControlVisible = true;

        // Try to find quantity control by data attribute as backup
        if (!this.quantityControl) {
            this.quantityControl = this.productElement.querySelector(
                `[data-product-quantity-control][data-product-id="${this.productId}"]`
            );
        }

        if (this.quantityControl) {
            // Remove hidden class and reset display
            this.quantityControl.classList.remove("hidden");
            this.quantityControl.style.display = "";
        }

        const addToCartButton = this.productElement.querySelector(
            "[data-ajax-add-to-cart]",
        );

        if (addToCartButton) {
            // Hide the add to cart button
            addToCartButton.classList.add("hidden");
            addToCartButton.style.display = "none";
        }
    }

    #showBuyButton() {
        // Mark that quantity control is now hidden
        this.state.isQuantityControlVisible = false;

        const addToCartButton = this.productElement.querySelector(
            "[data-ajax-add-to-cart]",
        );

        if (addToCartButton) {
            addToCartButton.classList.remove("hidden");
            addToCartButton.style.display = "";
        }

        if (this.quantityControl) {
            this.quantityControl.classList.add("hidden");
            this.quantityControl.style.display = "none";
        }
    }

    #updateQuantityDisplay(quantity) {
        // ALWAYS find the input by selector to make sure we have the right element
        const quantityControl = this.productElement.querySelector(
            `[data-product-quantity-control][data-product-id="${this.productId}"]`
        );

        if (!quantityControl) {
            return;
        }

        const quantityInput = quantityControl.querySelector('.quantity-input');

        if (quantityInput) {
            // Use multiple methods to ensure the value is updated
            quantityInput.setAttribute('value', quantity);
            quantityInput.value = quantity;

            // Force a repaint by toggling display
            const originalDisplay = quantityInput.style.display;
            quantityInput.style.display = 'none';
            quantityInput.offsetHeight; // Force reflow
            quantityInput.style.display = originalDisplay;
        }
    }

    async #increaseQuantity() {
        if (this.state.isLoading) return;

        const newQuantity = this.state.currentQuantity + 1;
        await this.#updateQuantity(newQuantity);
    }

    async #decreaseQuantity() {
        if (this.state.isLoading) return;

        const newQuantity = this.state.currentQuantity - 1;

        if (newQuantity <= 0) {
            await this.#removeFromCart();
        } else {
            await this.#updateQuantity(newQuantity);
        }
    }

    async #updateQuantity(newQuantity) {
        if (!this.cartKey) {
            return;
        }

        this.#setLoadingState(true);

        try {
            await this.cartManager.updateQuantity(this.cartKey, newQuantity);
            this.state.currentQuantity = newQuantity;

            // Show notification with quantity
            this.#showNotificationWithQuantity("Koszyk zaktualizowany", newQuantity);
        } catch (error) {
            this.#showNotification("Błąd podczas aktualizacji ilości", "error");
        } finally {
            this.#setLoadingState(false);
        }
    }

    async #removeFromCart() {
        if (!this.cartKey) {
            return;
        }

        this.#setLoadingState(true);

        try {
            await this.cartManager.removeFromCart(null, this.cartKey);
            this.state.currentQuantity = 0;
            this.state.isInCart = false;
        } catch (error) {
            this.#showNotification("Błąd podczas usuwania produktu", "error");
        } finally {
            this.#setLoadingState(false);
        }
    }

    #setLoadingState(isLoading) {
        this.state.isLoading = isLoading;

        if (this.quantityControl) {
            this.quantityControl.style.opacity = isLoading ? "0.4" : "1";
            this.quantityControl.classList.toggle("pointer-events-none", isLoading);
        }

        if (this.decreaseButton) {
            this.decreaseButton.disabled = isLoading;
        }
        if (this.increaseButton) {
            this.increaseButton.disabled = isLoading;
        }
    }

    #showNotification(message, type = "success") {
        Toastify({
            text: message,
            duration: 3000,
            close: true,
            gravity: "top",
            position: "center",
            backgroundColor:
                type === "error"
                    ? "linear-gradient(to right, #ff5f6d, #ffc371)"
                    : "linear-gradient(to right, #00b09b, #96c93d)",
        }).showToast();
    }

    #showNotificationWithQuantity(message, quantity) {
        // Create HTML content with main message, subtitle and button
        const htmlContent = `
      <div class="cart-notification-content">
        <div class="cart-notification-text">
          <div class="cart-notification-title">${message}</div>
          <div class="cart-notification-subtitle">Ilość sztuk w koszyku: ${quantity}</div>
        </div>
        <button
          id="mini-cart-btn-${this.productId}"
          class="cart-notification-button"
        >
          Zobacz koszyk
        </button>
      </div>
    `;

        const toast = Toastify({
            text: htmlContent,
            duration: 5000,
            close: false, // Remove X button
            gravity: "top",
            position: "right",
            backgroundColor: "linear-gradient(to right, #00b09b, #96c93d)",
            escapeMarkup: false,
        });

        toast.showToast();

        // Add click handler to the button after toast is shown
        setTimeout(() => {
            const button = document.getElementById(`mini-cart-btn-${this.productId}`);
            if (button) {
                button.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.#openMiniCart();
                });
            }
        }, 10);
    }

    #openMiniCart() {
        // Try to click the mini cart element directly to toggle it
        const miniCartRoot = document.querySelector('[data-widget-mini-cart="root"]');
        if (miniCartRoot) {
            miniCartRoot.click();
        }
    }

    #createFormData(data) {
        const formData = new FormData();
        Object.entries(data).forEach(([key, value]) => {
            formData.append(key, value);
        });
        formData.append("nonce", window?.app?.nonce);
        return formData;
    }

    destroy() {
        if (this.quantityControl) {
            this.quantityControl.remove();
        }

        Mediator.unsubscribe("ajax_sage_change_cart", this.#handleCartChange.bind(this));
    }
}
